// In-memory cache for the windowed stats GETs.
//
// Every stats endpoint behind `statsGet` is an idempotent aggregate read of data
// that changes when robots sync, i.e. at most nightly — and the dashboard asks
// for the same one repeatedly within a sitting: toggling the breakdown pill from
// "By school" to "By grade" and back, walking Overview -> Adoption -> Overview,
// revisiting a school year already looked at. Uncached, each of those is seconds
// of spinner on a large district for an answer the tab held moments earlier.
//
// Deliberately keyed on the request rather than on a component: the same payload
// is asked for by different callers (the container's spine, a page's drill-down,
// the Adoption ribbon), and a per-component memo would miss exactly the overlap
// worth catching. Living at module scope also means it survives a route change
// that unmounts the dashboard, which is when a memo held in a ref would throw
// away everything it holds.
//
// What this is NOT: a substitute for the server-side cache. That one is shared
// across users, tabs and sessions and can hold far more; this one only stops a
// single tab from asking twice.

// Long enough to cover a sitting with the dashboard -- the interaction this
// exists for is a minute of clicking between breakdowns and pages -- and short
// enough that an admin who changes something elsewhere in the app (a roster
// edit, an org's preferred standard set) and comes back sees it without a hard
// reload. The underlying data moves nightly, so this is bounded by attention
// span rather than by staleness.
const TTL_MS = 5 * 60 * 1000;

// Bounded by BYTES, not by intent: a segmented response for a 27-school district
// is ~1.1MB, so a naive "keep 50" would put tens of megabytes in a browser tab
// that also has to render them. Twelve entries covers the working set of one
// dashboard sitting (two spine calls x a few breakdowns, plus the lazy panels)
// at a ceiling of a few MB.
const MAX_ENTRIES = 12;

// key -> { at, promise }. Insertion-ordered, which is what makes the LRU eviction
// below a delete-and-reinsert rather than a scan.
const entries = new Map();

/**
 * A stable key for (token, path, params).
 *
 * Params are sorted because call sites build the same logical request with the
 * keys in different orders -- the container spreads `winArgs` last, a page
 * spreads it first -- and an insertion-ordered key would treat those as two
 * different requests and cache both.
 *
 * The token is part of the key so a second login in the same tab cannot read the
 * first user's payloads. Cheaper and harder to get wrong than clearing on logout,
 * which has to be remembered at every exit path.
 */
export const statsCacheKey = (token, path, params) => {
  const norm = Object.entries(params || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : String(v)])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return JSON.stringify([token, path, norm]);
};

/**
 * Run `fetcher()` unless an identical request is already cached or in flight.
 *
 * The PROMISE is cached, not the resolved value, so two callers that ask at the
 * same moment share one request instead of racing to fill the same slot. That is
 * not a hypothetical: the spine fires its two calls concurrently and a page can
 * mount asking for one of them in the same tick.
 *
 * A rejected promise is evicted rather than remembered. Caching a failure would
 * turn one dropped request into five minutes of a page that cannot recover, and
 * every caller here already handles a failed fetch.
 */
export const cachedStatsGet = (key, fetcher) => {
  const hit = entries.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) {
    // refresh recency so the LRU keeps what is actually being used
    entries.delete(key);
    entries.set(key, hit);
    return hit.promise;
  }
  if (hit) entries.delete(key);

  const promise = fetcher().catch((err) => {
    if (entries.get(key) && entries.get(key).promise === promise)
      entries.delete(key);
    throw err;
  });
  entries.set(key, { at: Date.now(), promise });
  while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value);
  return promise;
};

/** Drop everything. For tests, and for a caller that knows it just wrote. */
export const clearStatsCache = () => entries.clear();
